// "Group by" list (PX-386) — lives as a tab in the right tool panel next to
// Filters / Columns. Add dimensions, drag to reorder → the table nests groups
// in that exact order (group-in-group). Reuses the admin's HTML5-DnD pattern
// (DragHandle sets the dragged id; container onDrop calls moveItem).
import { DragHandle } from '@/components/FormControls'
import { moveItem } from '@/lib/reorder'
import { GROUP_DIMS, GROUP_DIM_BY_ID, type GroupDimId } from './model'

export function GroupByPanel({
  grouping,
  onChange,
}: {
  grouping: string[]
  onChange: (next: string[]) => void
}) {
  const available = GROUP_DIMS.filter((d) => !grouping.includes(d.id))

  const add = (id: string) => {
    if (id && !grouping.includes(id)) onChange([...grouping, id])
  }
  const remove = (id: string) => onChange(grouping.filter((g) => g !== id))
  const reorder = (sourceId: string, targetId: string) => {
    if (sourceId !== targetId) onChange(moveItem(grouping, sourceId, targetId, (x) => x))
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Group by</span>
        {grouping.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[11px] text-ink-400 underline-offset-2 transition hover:text-ink-700 hover:underline"
          >
            Clear
          </button>
        )}
      </div>

      {grouping.length === 0 && (
        <p className="text-xs text-ink-300">No grouping — flat list. Add a dimension to nest (drag to reorder levels).</p>
      )}

      <div className="flex flex-col gap-1">
        {grouping.map((id, idx) => {
          const dim = GROUP_DIM_BY_ID[id as GroupDimId]
          if (!dim) return null
          return (
            <div
              key={id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                const sourceId = e.dataTransfer.getData('text/plain')
                if (sourceId) reorder(sourceId, id)
              }}
              className="flex items-center gap-2 rounded-md border border-brand-200 bg-brand-50 px-2 py-1.5 text-xs font-medium text-brand-700"
            >
              <span className="w-4 text-center text-[10px] text-brand-400">{idx + 1}</span>
              <DragHandle id={id} />
              <span className="flex-1">{dim.label}</span>
              <button
                type="button"
                onClick={() => remove(id)}
                aria-label={`Remove ${dim.label} grouping`}
                className="text-brand-400 transition hover:text-brand-700"
              >
                ✕
              </button>
            </div>
          )
        })}
      </div>

      {available.length > 0 && (
        <select
          value=""
          onChange={(e) => {
            add(e.target.value)
            e.target.value = ''
          }}
          className="rounded-md border border-dashed border-ink-300 bg-white px-2 py-1.5 text-xs text-ink-500 focus:border-brand-500 focus:outline-none"
        >
          <option value="">+ Add grouping…</option>
          {available.map((d) => (
            <option key={d.id} value={d.id}>{d.label}</option>
          ))}
        </select>
      )}
    </div>
  )
}
